import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { UserPlus, Trash2, Users, ShieldCheck, X } from "lucide-react";

export const TeamSection = () => {
  const [accountants, setAccountants] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/accountants");
    setAccountants(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      await api.post("/accountants", form);
      setForm({ name: "", email: "", password: "" });
      setShowForm(false);
      await load();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteOpen) return;
    setDeleting(true);
    try {
      await api.delete(`/accountants/${deleteOpen.id}`);
      toast.success(`Contabilista "${deleteOpen.name}" removido.`);
      setDeleteOpen(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setDeleting(false); }
  };

  return (
    <div data-testid="accountants-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h2 className="font-display text-2xl">Contabilistas</h2>
          <p className="text-[color:var(--ink-muted)] mt-2 max-w-xl">Crie contas para a sua equipa. Cada contabilista só acede às empresas que lhe forem atribuídas no separador Empresas.</p>
        </div>
        <button onClick={() => setShowForm((v) => !v)} className="btn-primary inline-flex items-center gap-2" data-testid="toggle-accountant-form-btn">
          <UserPlus size={16} /> Novo contabilista
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="card-soft mt-6 grid sm:grid-cols-3 gap-4 items-end" data-testid="accountant-form">
          <div>
            <label className="label-eyebrow block mb-2">Nome</label>
            <input className="input-soft" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="accountant-name-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Email</label>
            <input type="email" className="input-soft" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="accountant-email-input" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Palavra-passe (mín. 6)</label>
            <input type="text" className="input-soft" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} minLength={6} required data-testid="accountant-password-input" />
          </div>
          {error && <div className="sm:col-span-3 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] rounded-lg p-3" data-testid="accountant-form-error">{error}</div>}
          <div className="sm:col-span-3 flex gap-2">
            <button type="submit" disabled={saving} className="btn-primary" data-testid="create-accountant-btn">{saving ? "A criar…" : "Criar contabilista"}</button>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancelar</button>
          </div>
        </form>
      )}

      <div className="mt-6 space-y-3">
        {accountants.length === 0 ? (
          <div className="card-soft text-center py-12 text-[color:var(--ink-muted)]" data-testid="empty-accountants">
            <Users className="mx-auto" size={28} />
            <p className="mt-3">Ainda sem contabilistas.</p>
          </div>
        ) : accountants.map((a) => (
          <div key={a.id} className="card-soft flex flex-wrap items-center gap-4" data-testid={`accountant-row-${a.id}`}>
            <div className="w-12 h-12 rounded-xl bg-[color:var(--paper)] border border-[color:var(--border)] flex items-center justify-center">
              <Users size={20} className="text-[color:var(--primary)]" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <div className="font-medium flex items-center gap-2">
                {a.name}
                {a.is_admin && <span className="chip chip-processed inline-flex items-center gap-1"><ShieldCheck size={12} /> Gestor</span>}
              </div>
              <div className="text-sm text-[color:var(--ink-muted)] font-mono-data">{a.email}</div>
            </div>
            <div className="text-sm">
              <div className="label-eyebrow">Empresas</div>
              <div className="font-mono-data text-lg">{a.companies_count}</div>
            </div>
            {!a.is_admin && (
              <button onClick={() => setDeleteOpen(a)} className="p-2 rounded-lg hover:bg-[#FEE2E2] text-[#991B1B] transition-colors" data-testid={`delete-accountant-${a.id}`} title="Remover">
                <Trash2 size={16} />
              </button>
            )}
          </div>
        ))}
      </div>

      {deleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 fade-in" onClick={() => !deleting && setDeleteOpen(null)}>
          <div className="card-soft w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="delete-accountant-modal">
            <div className="flex items-center justify-between">
              <span className="label-eyebrow">remover contabilista</span>
              <button onClick={() => setDeleteOpen(null)} className="p-1 rounded hover:bg-[color:var(--border)]"><X size={16} /></button>
            </div>
            <h2 className="font-display text-xl mt-2">{deleteOpen.name}</h2>
            <p className="text-sm text-[color:var(--ink-muted)] mt-2">
              Tem a certeza que quer remover este contabilista? As empresas atribuídas ficarão sem este responsável. Esta ação é irreversível.
            </p>
            <div className="mt-5 flex gap-2">
              <button onClick={confirmDelete} disabled={deleting} className="btn-primary flex-1 bg-[#991B1B] hover:bg-[#7F1515] border-[#991B1B]" data-testid="confirm-delete-accountant-btn">
                {deleting ? "A remover…" : "Remover contabilista"}
              </button>
              <button onClick={() => setDeleteOpen(null)} disabled={deleting} className="btn-secondary" data-testid="cancel-delete-accountant-btn">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
